/**
 * Geschmack-Finder als Web Component.
 *
 * Erweitert Horizons `Component` (Light-DOM, deklarative refs + on:click).
 * Zugehörige Dateien:
 *   - sections/mk-geschmack-finder.liquid     (Bühne + ein div je Frage)
 *   - sections/mk-finder-results.liquid       (Ziel des Ergebnis-Abrufs)
 *   - snippets/mk-finder-question.liquid      (eine Frage samt Antwort-Raster)
 *   - snippets/mk-finder-loader.liquid        (Ladeanzeige im Ergebnis-Schritt)
 *   - config/settings_schema.json             (10 Frage-Slots × 5 Felder)
 *   - locales/de.json + en.default.json       (Laufzeit-Schlüssel mk.finder.*)
 *
 * Schritte (totalSteps = questionCount + 1 Ergebnis):
 *   0..N-1  Frage    (Genussempfehlung, Zubereitungsart, Hauptaroma als Vorgabe)
 *   N       Ergebnis (Karussell der gefilterten Kaffee-Produkte,
 *                     daneben die Anmelde-Kachel — KEIN eigener Schritt mehr:
 *                     Kundenwunsch war, erst die Kaffees zu zeigen)
 *
 * Querschnittlich:
 *   - Abhängigkeitsregeln (Frage 1 → Frage 2) über fest hinterlegte
 *     Metaobjekt-GIDs in `ID` und `ZUBEREITUNG_HIDE_RULES` weiter unten.
 *     Erweitern heisst: neue GIDs eintragen und die Regel-Zuordnung ergänzen.
 *   - Formular-Anbindung (Shopify Forms Embed 997341, im App-Block-Slot):
 *       a) 4 eigene Formularfelder vorbelegen (#syncAnswersToFormFields)
 *       b) alle diese Felder verstecken (Hüllklasse _formFieldContainer_*)
 *       c) DSGVO-Häkchen einsetzen, Absenden bis dahin sperren
 *          (#installDsgvoCheckbox, arbeitet im Shadow-DOM)
 *       d) bei jeder Eingabe neu abgleichen, mit Schutz gegen die Schleife,
 *          die unser eigenes input-Ereignis auslösen würde
 *       e) Ergebnis-Überschrift ersetzt den festen Formulartitel (#syncFormHeading)
 *     Die App vergibt gehashte Klassennamen (`_formFieldContainer_1q1d2_172`); der Hash
 *     wechselt mit jedem App-Build. Deshalb IMMER `[class*="_name_"]` (Teilstring), NIE
 *     `[class^=…]`: das prüft den Anfang des GESAMTEN class-Attributs, nicht die einzelnen
 *     Klassen — sobald die App eine weitere Klasse davorsetzt, greift der Treffer still
 *     nicht mehr. Genau so ist die Abstands-Korrektur in #injectFormLayoutFix ausgefallen.
 *   - Kein Zustand über das Neuladen hinweg: Jeder Seitenaufruf beginnt bei Frage 1.
 *     Ein Eintrag aus der früheren Sicherung wird beim Start entfernt.
 *   - Warenkorb-Attribute: _geschmack_finder_<key> + _utm_source via /cart/update.js
 *   - Tracking: gtag/fbq mit event_category 'coffee_finder' beim ersten Auslösen,
 *     'coffee_finder_revised' bei jedem weiteren (erkennt geänderte Antworten).
 *   - Gutschein: WILLKOMMEN-CF5-N für Neukunden mit utm_source=meta.
 *   - Ersatzauswahl: die ungefilterte Kaffee-Kollektion wird beim Laden im Hintergrund
 *     geholt; ohne Treffer erscheint dieses Karussell statt eines blossen Knopfes.
 *   - Kollektions-Pfad: Section-Einstellung (`data-collection-url`), sonst
 *     COLLECTION_FETCH_BASE. Er MUSS ohne Weiterleitung antworten — sonst fallen
 *     Mehrfachantworten auf einen Wert zusammen (#fetchFiltered erkennt und heilt das).
 *   - Layout: #stabilizeHeight verankert die Bühnenhöhe an der höchsten Frage,
 *     damit die Seite zwischen den Schritten nicht springt.
 */
import { Component } from '@theme/component';

// Fest hinterlegte Metaobjekt-GIDs für die Abhängigkeit Frage 1 → Frage 2.
// (Entspricht GCCs hideShowAnswers(), aber mit Murnauers Werte-IDs. Muss
// nachgezogen werden, falls sich die Metaobjekt-IDs nach einer Datenmigration
// ändern.)
const ID = {
  // geschmack_finder_genussempfehlung
  espresso:       'gid://shopify/Metaobject/497538826575',
  milchbasiert:   'gid://shopify/Metaobject/497538892111',
  cafeCrema:      'gid://shopify/Metaobject/497538957647',
  bruehkaffee:    'gid://shopify/Metaobject/497539023183',
  coldbrew:       'gid://shopify/Metaobject/497539055951',
  // geschmack_finder_zubereitungsart
  zMoka:          'gid://shopify/Metaobject/497537745231',
  zSiebtraeger:   'gid://shopify/Metaobject/497537777999',
  zVollautomat:   'gid://shopify/Metaobject/497537876303',
  zBruehkaffee:   'gid://shopify/Metaobject/497537909071',
  zFrenchPress:   'gid://shopify/Metaobject/497537941839',
  zColdBrew:      'gid://shopify/Metaobject/497537974607',
};

// Regel: Zu dieser Genussempfehlung werden jene Zubereitungsarten ausgeblendet.
const ZUBEREITUNG_HIDE_RULES = {
  [ID.bruehkaffee]:  [ID.zMoka, ID.zSiebtraeger, ID.zVollautomat],
  [ID.coldbrew]:     [ID.zMoka, ID.zSiebtraeger, ID.zVollautomat],
  [ID.espresso]:     [ID.zBruehkaffee, ID.zFrenchPress, ID.zColdBrew],
  [ID.cafeCrema]:    [ID.zBruehkaffee, ID.zFrenchPress, ID.zColdBrew],
  [ID.milchbasiert]: [ID.zColdBrew],
};

const STORAGE_KEY = 'mk_geschmack_finder_state';
// Ausweichwert, falls die Section keine Kollektion mitgibt. MUSS der Pfad sein, der
// OHNE Weiterleitung antwortet: `/collections/kaffee` ist auf
// `/collections/kaffee-gesamtsortiment` umgezogen, und eine Weiterleitung fasst
// wiederholte Filter-Parameter zu EINEM zusammen — aus „Süss ODER Nussig" wird still
// ein Ein-Wert-Filter (siehe #fetchFiltered).
const COLLECTION_FETCH_BASE = '/collections/kaffee-gesamtsortiment';
const COUPON_CODE = 'WILLKOMMEN-CF5-N';
// Kennzeichnet den Aufruf der Kollektionsseite als Ergebnis dieses Quiz — ausgewertet von
// assets/mk-personalized-heading.js (Hero-Überschrift).
const RECOMMENDATION_MARKER = 'empfehlung=1';

class MkGeschmackFinder extends Component {
  requiredRefs = ['stage', 'prev', 'next'];

  #current = 0;
  #totalSteps = 0;
  #questionCount = 0;
  #steps = [];
  #resultSectionId = null;
  #tracked = false;
  #formInitialized = false;
  #stableHeight = 0;
  /** @type {DocumentFragment | null} Vorab geholte Ersatzauswahl für den Fall ohne Treffer. */
  #fallbackProducts = null;
  /** @type {Promise<void> | null} Läuft, solange die Ersatzauswahl geladen wird. */
  #fallbackReady = null;
  /** @type {string | null} Überschrift des Ergebnisses; ersetzt den Titel des Anmeldeformulars. */
  #headlineText = null;
  /** @type {boolean} true = der Text ist nur ein Hinweis (kein Treffer) und steht klein ÜBER dem Formulartitel. */
  #headlineIsNotice = false;
  /** @type {string | null} Der Titel, den die Forms-App selbst mitbringt — beim ersten Zugriff gesichert. */
  #formHeadingOriginal = null;
  /** @type {Set<string>} Bereits ausgelöste Ereignisse — jedes weitere zählt als 'revised'. */
  #trackedEventNames = new Set();
  /** @type {ReturnType<typeof setTimeout> | null} Läuft zwischen Auswahl und Sprung zur nächsten Frage. */
  #autoAdvanceTimer = null;
  /** Zeitpunkt der letzten Zeiger-/Tippbedienung auf einer Antwort. */
  #lastPointerAt = 0;
  /** @type {string | null} Ziel einer erkannten Weiterleitung — gilt ab dann als Kollektions-Pfad. */
  #learnedBase = null;

  connectedCallback() {
    super.connectedCallback();

    this.#totalSteps = parseInt(this.dataset.totalSteps, 10) || 0;
    this.#questionCount = parseInt(this.dataset.questionCount, 10) || 0;
    this.#resultSectionId = this.dataset.resultSectionId;
    this.#steps = Array.from(this.querySelectorAll('.mk-finder__question'));

    this.#forgetStoredState();
    this.#applyDependencyRules();
    this.#wireUpAnswers();
    this.#updateUI();
    this.#fallbackReady = this.#prefetchFallbackProducts();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    // Ein noch laufender Sprung würde sonst auf einem bereits entfernten Element landen
    // (Editor-Neuaufbau, Section-Rendering-API).
    clearTimeout(this.#autoAdvanceTimer);
    this.#autoAdvanceTimer = null;
  }

  // === Kollektions-Pfad ===
  // Reihenfolge: gelerntes Weiterleitungs-Ziel → Angabe der Section (Kollektions-Auswahl
  // im Editor) → Ausweichwert.
  #collectionBase() {
    return this.#learnedBase || this.dataset.collectionUrl || COLLECTION_FETCH_BASE;
  }

  // Landet ein Abruf nach einer Weiterleitung auf einem anderen Kollektions-Pfad, gilt
  // ab jetzt dieser. Rückgabe: true, wenn sich der Pfad geändert hat.
  #learnBase(finalUrl) {
    try {
      const path = new URL(finalUrl, location.origin).pathname;
      if (!path.includes('/collections/') || path === this.#collectionBase()) return false;
      this.#learnedBase = path;
      return true;
    } catch (_) {
      return false;
    }
  }

  // Holt das Karussell der ungefilterten Kaffee-Kollektion im Voraus, damit bei
  // einer Antwortkombination ohne Treffer echte Produkte dastehen und nicht nur
  // ein Knopf. Nebeneffekt mit Absicht: eine Weiterleitung wird hier schon erkannt,
  // also noch bevor der erste gefilterte Abruf läuft.
  async #prefetchFallbackProducts() {
    if (!this.#resultSectionId) return;
    try {
      const res = await fetch(`${this.#collectionBase()}?section_id=${this.#resultSectionId}`);
      this.#learnBase(res.url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const frag = document.createDocumentFragment();
      while (doc.body.firstChild) frag.appendChild(doc.body.firstChild);
      this.#fallbackProducts = frag;
    } catch (_) { /* still — die Ersatzauswahl ist eine Zugabe, kein Muss */ }
  }

  next(event) {
    event?.preventDefault();
    if (!this.#currentStepIsAnswered()) {
      this.#setError(true);
      return;
    }
    this.#setError(false);
    this.#trackStartedOnce();
    this.#trackCurrentAnswer();
    this.#goTo(Math.min(this.#current + 1, this.#totalSteps - 1));
  }

  prev(event) {
    event?.preventDefault();
    this.#setError(false);
    this.#goTo(Math.max(this.#current - 1, 0));
  }

  // === Schritt-Navigation ===

  #goTo(step) {
    // Blättert jemand selbst, bevor der automatische Sprung fällig ist, fällt er aus —
    // sonst würde er die Frage danach überspringen.
    clearTimeout(this.#autoAdvanceTimer);
    this.#autoAdvanceTimer = null;

    this.#current = step;
    this.dataset.step = String(step);
    this.#updateUI();

    const stepEl = this.#steps[step];
    if (!stepEl) return;

    // Der letzte Schritt zeigt die Empfehlungen und darunter das Anmeldeangebot —
    // beides wird hier scharf geschaltet.
    if (stepEl.dataset.stepType === 'result') {
      this.#fetchAndRenderResults();
      this.#initForm();
    }
  }

  #updateUI() {
    this.#steps.forEach((el, i) => {
      const isActive = i === this.#current;
      el.hidden = !isActive;
      el.setAttribute('aria-hidden', String(!isActive));
    });

    // Erklärtext der aktuellen Frage einblenden (steht unter der Navigation, einer je Frage).
    this.querySelectorAll('[data-explanation-for]').forEach((el) => {
      el.hidden = String(this.#current) !== el.dataset.explanationFor;
    });

    const { prev, next } = this.refs;
    if (prev) prev.disabled = this.#current === 0;
    if (next) next.disabled = this.#current >= this.#totalSteps - 1;

    // Letzte Frage: Knopf statt Zähler (Begründung im Markup der Section).
    const onQuestion = this.#current < this.#questionCount;
    const onLastQuestion = onQuestion && this.#current === this.#questionCount - 1;

    const text = this.querySelector('[data-pagination-text]');
    if (text) {
      text.textContent = onQuestion && !onLastQuestion
        ? this.#paginationText(this.#current + 1, this.#questionCount)
        : '';
      text.hidden = !onQuestion || onLastQuestion;
    }

    const cta = this.querySelector('[data-pagination-cta]');
    if (cta) cta.hidden = !onLastQuestion;

    this.#stabilizeHeight();
  }

  // Verankert die Mindesthöhe der Bühne an der höchsten bisher gesehenen Frage,
  // damit die Seite beim Blättern nicht springt. Der Ergebnis-Schritt behält
  // seine natürliche Höhe — er bringt eigene Breiten und Inhalte mit.
  #stabilizeHeight() {
    const stage = /** @type {HTMLElement} */ (this.refs.stage);
    if (!stage) return;
    const stepEl = this.#steps[this.#current];
    // Frage-Schritte kommen aus dem Snippet mk-finder-question und setzen kein
    // data-step-type — fehlt es, ist es eine Frage. Nur das Ergebnis setzt es.
    const stepType = stepEl?.dataset.stepType || 'question';
    if (!stepEl || stepType !== 'question') {
      stage.style.minHeight = '';
      return;
    }
    stage.style.minHeight = '';
    const h = stage.offsetHeight;
    if (h > this.#stableHeight) this.#stableHeight = h;
    stage.style.minHeight = `${this.#stableHeight}px`;
  }

  #paginationText(current, total) {
    return this.#localized(`Frage ${current} von ${total}`, `Question ${current} of ${total}`);
  }

  // === Antworten verdrahten + Abhängigkeit ===

  #wireUpAnswers() {
    // Zeiger-/Tippbedienung merken — siehe #maybeAutoAdvance.
    this.addEventListener('pointerdown', (e) => {
      const el = e.target;
      if (el instanceof Element && el.closest('.mk-finder__answer, .mk-finder__input')) {
        this.#lastPointerAt = performance.now();
      }
    });

    this.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.matches('.mk-finder__input')) return;
      this.#setError(false);
      // Muss VOR dem Weiterspringen laufen: die Antwort auf Frage 1 blendet Optionen
      // der Folgefrage aus.
      this.#applyDependencyRules();
      this.#maybeAutoAdvance(target);
    });
  }

  // Bei Einfachauswahl ist die getroffene Wahl zugleich die Bestätigung — der Weiter-Knopf
  // wäre ein zweiter Klick für dieselbe Aussage. Bei Mehrfachauswahl geht das nicht: dort
  // ist erst nach dem letzten Haken klar, dass die Antwort steht.
  //
  // Zwei Einschränkungen:
  //  • Nur bei Zeiger-/Tippbedienung. Mit der Tastatur wandert der Fokus per Pfeiltaste durch
  //    die Optionen und löst dabei JEDE Zwischenstation als Auswahl aus — ein automatischer
  //    Sprung würde verhindern, die Auswahl überhaupt durchzusehen (WCAG 3.2.2 "Bei Eingabe").
  //    Über die Tastatur bleibt der Weiter-Knopf der Weg.
  //  • Kurze Pause, damit die getroffene Auswahl noch sichtbar wird, bevor die Frage wechselt.
  #maybeAutoAdvance(input) {
    if (input.type !== 'radio') return;
    const step = this.#steps[this.#current];
    if (!step || step.dataset.answerType !== 'radio' || !step.contains(input)) return;
    if (performance.now() - this.#lastPointerAt > 700) return;
    // Einmal verwendet, verbraucht: ein Tippen löst höchstens einen Sprung aus. Sonst
    // könnte ein Pfeiltasten-Wechsel kurz nach einem Klick noch ins Zeitfenster fallen.
    this.#lastPointerAt = 0;

    clearTimeout(this.#autoAdvanceTimer);
    this.#autoAdvanceTimer = setTimeout(() => {
      this.#autoAdvanceTimer = null;
      this.next();
    }, 320);
  }

  #applyDependencyRules() {
    const selectedGenuss = this.querySelector(
      'input[name="filter.p.m.product.genussempfehlung"]:checked'
    );
    const hideIds = selectedGenuss ? ZUBEREITUNG_HIDE_RULES[selectedGenuss.value] || [] : [];

    this.querySelectorAll('input[name="filter.p.m.product.zubereitungsart"]').forEach((input) => {
      const label = input.nextElementSibling;
      const hide = hideIds.includes(input.value);
      if (hide && input.checked) input.checked = false;
      input.hidden = hide;
      input.disabled = hide;
      if (label) label.hidden = hide;
    });

    // Spaltenzahl des Rasters auf die jetzt sichtbaren Antworten nachziehen, höchstens 5.
    // Dazu die letzte sichtbare Antwort markieren, damit die mobile Regel
    // „ungerade Anzahl → volle Breite" auch greift, wenn spätere Geschwister
    // durch die Abhängigkeitsregeln ausgeblendet sind.
    const zStep = this.querySelector('[data-metaobject-handle="geschmack_finder_zubereitungsart"]');
    const zAnswers = zStep?.querySelector('.mk-finder__answers');
    if (zAnswers) {
      const visible = Array.from(zAnswers.querySelectorAll('.mk-finder__answer:not([hidden])'));
      if (visible.length > 0) {
        zAnswers.style.setProperty('--mk-finder-answers-count', String(Math.min(visible.length, 5)));
      }
      zAnswers.querySelectorAll('.mk-finder__answer--last-visible').forEach((el) =>
        el.classList.remove('mk-finder__answer--last-visible')
      );
      if (visible.length % 2 === 1) {
        visible[visible.length - 1].classList.add('mk-finder__answer--last-visible');
      }
    }
  }

  // === Fehler / Prüfung ===

  #currentStepIsAnswered() {
    const stepEl = this.#steps[this.#current];
    if (!stepEl) return true;
    if (stepEl.dataset.stepType === 'result') return true;
    return stepEl.querySelector('.mk-finder__input:checked') !== null;
  }

  #setError(on) {
    this.classList.toggle('has-error', !!on);
  }

  // === Zustand ===
  // Der Finder sichert nichts mehr über das Neuladen hinweg: Wer die Seite neu
  // lädt, beginnt bei Frage 1. Frühere Fassungen legten Schritt und Antworten in
  // localStorage ab; dieser Eintrag wird beim Start entfernt, damit die
  // Geschmacksangaben nicht unbegrenzt im Browser des Besuchers liegen bleiben.
  // (Die Antworten der abgeschlossenen Runde stehen weiterhin als
  // Warenkorb-Attribute an einer späteren Bestellung — siehe #storeAnswersToCart.)
  #forgetStoredState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }

  // === Ergebnis abrufen ===

  async #fetchAndRenderResults() {
    const resultEl = this.refs.result;
    if (!resultEl) return;

    const grouped = {};
    this.querySelectorAll('.mk-finder__input:checked').forEach((input) => {
      if (!grouped[input.name]) grouped[input.name] = [];
      grouped[input.name].push(input.value);
    });

    // Ein EIGENER Parameter je Wert — dieselbe Adresse baut auch Horizons eigener
    // Filter-Bereich. Ein kommagetrennter Sammelwert ist keine Filter-Syntax und liefert
    // 0 Treffer. Verschiedene Fragen bleiben untereinander UND-verknüpft.
    //
    // Ob Mehrfachantworten ODER oder UND ergeben, entscheidet NICHT dieser Code, sondern
    // die Logik-Wahl des Filters in Search & Discovery (Admin ▸ Apps): „Hauptaroma" MUSS
    // auf OR-Bedingung stehen, sonst zeigt die Mehrfachauswahl wieder die Schnittmenge —
    // Kundenwunsch war das Addieren. Der Schalter ist app-owned, also nicht aus dem Theme
    // prüfbar; empirisch: zwei Werte auf der Kollektionsseite müssen die Summe zeigen.
    const params = new URLSearchParams();
    Object.entries(grouped).forEach(([name, values]) => values.forEach((v) => params.append(name, v)));

    this.#storeAnswersToCart(grouped);

    try {
      const { res, baseUrl } = await this.#fetchFiltered(params);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const productCount = this.#readProductCount(doc);

      this.#trackEvent('coffee_finder_view_results', String(productCount));

      // Ohne Treffer zeigen wir die Ersatzauswahl — die wird beim Laden im
      // Hintergrund geholt und ist bei einem Neuladen direkt auf diesem Schritt
      // oft noch unterwegs. Ohne dieses Warten bliebe der Bereich leer, obwohl
      // der Hinweis zum Stöbern einlädt.
      if (!productCount) await this.#fallbackReady;

      this.#renderResultDom(resultEl, productCount, doc.body, baseUrl);
    } catch (err) {
      console.error('mk-geschmack-finder result fetch failed:', err);
      resultEl.replaceChildren(this.#textP(this.#localized('Es ist ein Fehler aufgetreten.', 'An error occurred.')));
    }
  }

  // Ruft die gefilterte Kollektion ab und hält dabei die Mehrfachauswahl heil: eine
  // Weiterleitung fasst wiederholte Filter-Parameter zu EINEM zusammen, aus „Süss ODER
  // Nussig" wird still ein Ein-Wert-Filter ([Feedback Thomas 2026-08]; Herleitung in
  // .claude/memory/horizon_block_patterns.md → „Weiterleitung dedupliziert"). Deshalb die
  // Antwort-Adresse gegen die gesendeten Werte halten und bei Verlust am Ziel der
  // Weiterleitung EINMAL wiederholen — das greift auch bei künftigen Umbenennungen.
  async #fetchFiltered(params) {
    const build = () => `${this.#collectionBase()}?${params.toString()}`;
    let baseUrl = build();
    let res = await fetch(`${baseUrl}&section_id=${this.#resultSectionId}`);

    const moved = this.#learnBase(res.url);
    if (moved && this.#valuesLost(params, res.url)) {
      baseUrl = build();
      res = await fetch(`${baseUrl}&section_id=${this.#resultSectionId}`);
    }
    return { res, baseUrl };
  }

  // true, wenn die Antwort-Adresse zu einem Filter weniger Werte trägt als gesendet.
  #valuesLost(params, finalUrl) {
    try {
      const kept = new URL(finalUrl, location.origin).searchParams;
      return [...new Set(params.keys())].some(
        (key) => kept.getAll(key).length < params.getAll(key).length
      );
    } catch (_) {
      return false;
    }
  }

  // Die WAHRE Trefferzahl steht als data-Attribut in der nachgeladenen Section. Karten zu
  // zählen ging daneben, sobald deren Zahl gedeckelt ist („Diese 6 Kaffees…", obwohl 7
  // passen) — die Ersatzauswahl ohne Treffer ist weiterhin gedeckelt. Karten zählen bleibt
  // der Ausweichweg, falls das Attribut fehlt.
  #readProductCount(doc) {
    const el = doc.querySelector('[data-products-count]');
    if (el) return parseInt(el.getAttribute('data-products-count'), 10) || 0;
    return doc.querySelectorAll('[data-mk-finder-card]').length;
  }

  // Baut das DOM des Ergebnis-Schritts auf sichere Weise: eigene Texte über
  // textContent, das geholte Karussell als bereits geparster Dokumentteil.
  #renderResultDom(resultEl, productCount, parsedBody, baseUrl) {
    const frag = document.createDocumentFragment();

    const headline = document.createElement('p');
    headline.className = 'mk-finder__result-headline';
    if (productCount) {
      headline.textContent = this.#resultHeadline(productCount);
    } else {
      headline.textContent = this.#noResultsHeadline();
      headline.classList.add('mk-finder__result-headline--notice');
    }
    frag.appendChild(headline);

    const coupon = this.#renderCouponIfEligible();
    if (coupon) frag.appendChild(coupon);

    if (productCount) {
      // Die geparsten Kinder umhängen — echte Knoten, kein innerHTML.
      const productsWrap = document.createElement('div');
      productsWrap.className = 'mk-finder__result-products';
      while (parsedBody.firstChild) {
        productsWrap.appendChild(parsedBody.firstChild);
      }
      frag.appendChild(productsWrap);

      // Marker für die Kollektionsseite: sie zeigt damit die Überschrift aus dem
      // Hero-Setting `personalized_heading` statt „Kaffee Gesamtsortiment"
      // ([Feedback Thomas 2026-08]). Mechanik und Grenzen: mk-personalized-heading.js.
      const cta = document.createElement('a');
      cta.href = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${RECOMMENDATION_MARKER}`;
      cta.className = 'button button--mk-sm mk-finder__result-cta';
      cta.textContent = this.#localized('Zu allen Empfehlungen', 'See all recommendations');
      frag.appendChild(cta);
    } else {
      // Ohne Treffer: die vorab geholte Ersatzauswahl aus der ungefilterten
      // Kaffee-Kollektion zeigen, damit etwas Brauchbares dasteht.
      if (this.#fallbackProducts && this.#fallbackProducts.childNodes.length > 0) {
        const productsWrap = document.createElement('div');
        productsWrap.className = 'mk-finder__result-products';
        // Kopien einhängen, damit der zwischengespeicherte Teil wiederverwendbar bleibt
        this.#fallbackProducts.childNodes.forEach((n) => productsWrap.appendChild(n.cloneNode(true)));
        frag.appendChild(productsWrap);
      }

      const cta = document.createElement('a');
      cta.href = this.#collectionBase();
      cta.className = 'button button--mk-sm mk-finder__result-cta';
      cta.textContent = this.#localized('Kaffee entdecken', 'Discover coffee');
      frag.appendChild(cta);
    }

    // Der Text gehört an das Anmeldeformular, nicht über die Kaffees — wohin genau,
    // entscheidet #syncFormHeading.
    this.#headlineText = headline.textContent;
    this.#headlineIsNotice = !productCount;

    resultEl.replaceChildren(frag);
    this.#syncFormHeading();
  }

  // Bringt den Ergebnistext in die Anmelde-Kachel. Läuft aus beiden Richtungen: beim
  // Rendern der Ergebnisse und sobald das Formular geladen ist (was länger dauern kann).
  // Ohne Formular bleibt der Text über den Kaffees stehen.
  //
  // Zwei Fälle, bewusst unterschiedlich ([Feedback ET 2026-08]):
  //  • MIT Treffern ist der Text eine Aussage über das Ergebnis („Diese 3 Kaffees passen
  //    zu deinem Geschmack") — er ersetzt den Formulartitel und steht damit groß.
  //  • OHNE Treffer ist er nur ein Hinweis auf die Ersatzauswahl. Als Titel las er sich
  //    wie eine Fehlermeldung und verdrängte die Rabatt-Zeile, um die es in der Kachel
  //    geht. Er steht deshalb klein DARÜBER, und als Titel wird der Text der App
  //    gesetzt — nicht bloß stehen gelassen: nach einem Durchlauf mit Treffern stünde
  //    dort sonst noch deren Überschrift.
  #syncFormHeading() {
    if (!this.#headlineText) return;
    const embed = this.querySelector('shopify-forms-embed');
    const heading = embed?.shadowRoot?.querySelector('[class*="_textHeading_"], h2');
    if (!heading) return;

    // Den mitgelieferten Titel beim ersten Zugriff sichern — ohne ihn ließe sich der
    // Treffer-Fall nicht zurücknehmen.
    if (this.#formHeadingOriginal === null) this.#formHeadingOriginal = heading.textContent;

    // Aufräumen zuerst: Wer zurückblättert, anders antwortet und ein anderes Ergebnis
    // bekommt, hätte sonst Hinweis oder Überschrift des vorigen Durchlaufs vor sich.
    heading.parentElement?.querySelector('.mk-finder__form-notice')?.remove();

    if (this.#headlineIsNotice) {
      heading.textContent = this.#formHeadingOriginal;
      const notice = document.createElement('p');
      notice.className = 'mk-finder__form-notice';
      notice.textContent = this.#headlineText;
      heading.parentElement?.insertBefore(notice, heading);
    } else {
      heading.textContent = this.#headlineText;
    }
    this.querySelector('.mk-finder__result-headline')?.remove();
  }

  #textP(text) {
    const p = document.createElement('p');
    p.textContent = text;
    return p;
  }

  #resultHeadline(count) {
    if (count === 1) {
      return this.#localized('Dieser Kaffee passt zu deinem Geschmack', 'This coffee matches your taste');
    }
    return this.#localized(
      `Diese ${count} Kaffees passen zu deinem Geschmack`,
      `These ${count} coffees match your taste`
    );
  }

  #noResultsHeadline() {
    return this.#localized(
      'Leider haben wir keinen exakten Treffer gefunden. Stöbere in unserer Auswahl:',
      'No exact matches found. Browse our selection:'
    );
  }

  #renderCouponIfEligible() {
    try {
      const utm = localStorage.getItem('utm_source');
      const isNew = this.dataset.newCustomer === 'true';
      if (utm === 'meta' && isNew) {
        const wrap = document.createElement('p');
        wrap.className = 'mk-finder__coupon';
        wrap.textContent = this.#localized(
          `Erhalte 5% Willkommensrabatt mit Code: ${COUPON_CODE}`,
          `Get 5% welcome discount with code: ${COUPON_CODE}`
        );
        return wrap;
      }
    } catch (_) {}
    return null;
  }

  // === Warenkorb-Attribute ===

  async #storeAnswersToCart(grouped) {
    const attributes = {};
    Object.entries(grouped).forEach(([name, values]) => {
      const key = `_${name.replace('filter.p.m.product.', 'geschmack_finder_')}`;
      attributes[key] = values.join(',');
    });
    try {
      const utm = localStorage.getItem('utm_source');
      if (utm) attributes._utm_source = utm;
    } catch (_) {}
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (err) {
      console.error('mk-geschmack-finder cart update failed:', err);
    }
  }

  // === Anbindung des eingebetteten Formulars (Shopify Forms, Formular 997341) ===

  #initForm() {
    // Steht das Formular schon, ist beim erneuten Erreichen des Ergebnisses nur die
    // Vorbelegung fällig — die einmaligen Eingriffe (Layout, Absende-Beobachter,
    // Einwilligung) bleiben stehen. Ohne dieses Nachziehen behalten die versteckten
    // Auswertungsfelder die Antworten des ERSTEN Durchlaufs: Wer zurückblättert,
    // anders antwortet und erneut absendet, meldet stillschweigend den alten Stand
    // an die Forms-App weiter.
    if (this.#formInitialized) {
      this.#refreshFormAnswers();
      return;
    }
    // Wartet auf beides: das Element <shopify-forms-embed> (steht nach einem
    // Neuladen wegen Horizons Section-Lebenszyklus noch nicht im Light-DOM) UND
    // das Formular in dessen Shadow-Root (holt Shopify Forms nebenläufig). Eine
    // Schleife deckt beide Wettläufe ab: 50 × 200 ms, danach still aufgeben.
    const tryInit = () => {
      if (this.#formInitialized) return true;
      const embed = this.querySelector('shopify-forms-embed');
      if (!embed) return false;
      const form = embed.shadowRoot?.querySelector('form');
      if (!form) return false;
      this.#formInitialized = true;
      this.#injectFormLayoutFix(embed);
      this.#wireFormSubmit(embed, form);
      this.#syncAnswersToFormFields(form);
      this.#installDsgvoCheckbox(embed, form);
      this.#syncFormHeading();
      // Vorsichtshalber: Baut Shopify Forms die Felder neu auf (etwa nach einem
      // Prüffehler), wären Vorbelegung und Ausblendung weg. Deshalb bei jeder
      // Eingabe erneut setzen — mit Schutz gegen die Schleife, die
      // #syncAnswersToFormFields selbst auslöst, weil es auf den vorbelegten
      // Feldern ein input-Ereignis abschickt, das bis zum Formular hochblubbert.
      form.addEventListener('input', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (target?.id?.startsWith('custom#geschmack_finder_')) return;
        this.#syncAnswersToFormFields(form);
      });
      return true;
    };
    if (tryInit()) return;
    let tries = 0;
    const timer = setInterval(() => {
      if (tryInit() || ++tries >= 50) clearInterval(timer);
    }, 200);
  }

  // === Optik-Korrektur am Formular der Forms-App ===
  // Zwei Dinge: der Innenabstand des Rasters (siehe unten) und die Farben. Die App
  // liefert ihr eigenes Schwarz für Titel, Text und Absende-Knopf; hier gelten
  // stattdessen die Farben des Themes ([Feedback ET 2026-08]). Möglich ist das, weil
  // Custom Properties die Shadow-Grenze durchdringen: `--color-*` kommt von der Kachel
  // `.mk-finder__offer.color-<scheme>` im Light-DOM. Also keine festen Hex-Werte hier —
  // wechselt der Merchant das Farbschema der Kachel, wandert das Formular mit.
  //
  // Die App setzt am Inhalts-Container jeder Rasterzelle einen unteren Innenabstand.
  // Bei der letzten Zeile addiert er sich auf den Abstand zum Kachelrand und lässt das
  // Formular unten auseinanderfallen.
  //
  // Drei Dinge sind hier bewusst gewählt:
  //  • TEILSTRING `*=`, nicht Präfix `^=`. Die App vergibt gehashte Klassennamen
  //    (`_gridItemContent_1q1d2_257`), der Hash wechselt mit jedem App-Build — nach dem
  //    vollen Namen zu suchen wäre beim nächsten Update still weg. Aber Achtung:
  //    `[class^=…]` prüft den Anfang des GESAMTEN class-Attributs, nicht die einzelnen
  //    Klassen. Das Element trägt `class="_gridItem_… _gridItemContent_…"`, der gesuchte
  //    Name steht an zweiter Stelle — mit `^=` griff die Regel nie.
  //  • `!important`. Die App liefert ihre Angaben als constructed stylesheet
  //    (adoptedStyleSheets); die stehen in der Kaskade NACH einem <style> im Shadow-Root
  //    und gewinnen bei gleicher Spezifität. Nur die Wichtig-Markierung schlägt das.
  //  • Eigene Methode statt Anhängen an den Style-Block in #installDsgvoCheckbox: der
  //    fällt aus, wenn Absende-Knopf oder Hinweistext fehlen.
  //
  // Verlässlich prüfbar nur im echten Browser, weil die Forms-App automatisierte Browser
  // nur zeitweise beliefert (siehe claude_shopify_workflow.md, Fallstrick 5).
  //
  // Zweiter Teil: die Forms-App liefert Texte, Felder und Absende-Knopf in ihrem
  // Fast-Schwarz. Ersetzt durch die Farbschema-Variablen (Custom Properties erben über
  // die Shadow-Grenze).
  #injectFormLayoutFix(embed) {
    const root = embed?.shadowRoot;
    if (!root || root.querySelector('style[data-mk-form-layout]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-mk-form-layout', '');
    style.textContent = `
      [class*="_gridItemContent_"] { padding-bottom: 0 !important; }

      /* Zwei Ausnahmen von der Textfarbe: Prüfhinweise der App sind rot, damit sie
         auffallen — im Braun des Themes wären sie nicht mehr als Fehler erkennbar.
         Und alles im Absende-Knopf, das seine eigene Farbe unten bekommt; ohne diese
         Ausnahme stünde die Beschriftung braun auf braun (dieser Selektor wiegt
         schwerer als der dortige, die Wichtig-Markierung auf beiden Seiten ändert
         daran nichts).
         ACHTUNG: keine Backticks in diesem Block — er steht in einem Template-Literal
         und würde davon beendet. */
      :is([class*="_text"], h1, h2, h3, p, label, span, li):not(
        [class*="error" i], [class*="invalid" i], [aria-invalid="true"], [role="alert"],
        [class*="error" i] *, [role="alert"] *, button, button *, .mk-finder__form-notice
      ) {
        color: var(--color-foreground) !important;
      }
      input, textarea, select {
        color: var(--color-foreground) !important;
        border-radius: var(--style-border-radius-inputs) !important;
      }
      a { color: inherit !important; }

      button[type="submit"], button:not([type]) {
        background-color: var(--color-primary-button-background) !important;
        border-color: var(--color-primary-button-border) !important;
        border-radius: var(--style-border-radius-buttons-primary) !important;
      }
      button[type="submit"], button:not([type]), button * {
        color: var(--color-primary-button-text) !important;
      }
      button[type="submit"]:hover, button:not([type]):hover {
        background-color: var(--color-primary-button-hover-background) !important;
      }

      /* Hinweis ohne Treffer: klein, gedämpft, über dem Titel der App. */
      .mk-finder__form-notice {
        margin: 0 0 var(--space-2) !important;
        font-size: var(--font-size--sm) !important;
        line-height: var(--line-height--140) !important;
      }
    `;
    root.appendChild(style);
  }

  // === DSGVO-Einwilligung ===
  // Setzt vor den Absenden-Knopf ein ausdrückliches Häkchen (Double-Opt-in) und
  // sperrt das Absenden, bis es gesetzt ist. Der ursprüngliche Hinweistext wird
  // zur Beschriftung, die Verweise auf die Richtlinien bleiben anklickbar.
  #installDsgvoCheckbox(embed, form) {
    const submitBtn = form.querySelector('button[type="submit"], button:not([type])');
    const disclaimer = embed.shadowRoot?.querySelector('[class*="_formDisclaimer_"]');
    if (!submitBtn || !disclaimer) return;
    if (form.querySelector('.mk-finder__dsgvo')) return; // steht schon

    const wrap = document.createElement('div');
    wrap.className = 'mk-finder__dsgvo';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.id = 'mk-finder-dsgvo';
    cb.required = true;
    const label = document.createElement('label');
    label.htmlFor = 'mk-finder-dsgvo';
    label.appendChild(this.#linkifyDisclaimer(disclaimer.textContent || ''));
    wrap.append(cb, label);

    submitBtn.disabled = true;
    cb.addEventListener('change', () => { submitBtn.disabled = !cb.checked; });

    submitBtn.parentElement.insertBefore(wrap, submitBtn);
    disclaimer.style.display = 'none';

    const style = document.createElement('style');
    style.textContent = `
      .mk-finder__dsgvo { display: flex; gap: 8px; align-items: flex-start; margin: 16px 0; font-size: 12px; text-align: left; }
      .mk-finder__dsgvo input { margin-top: 3px; flex-shrink: 0; }
      .mk-finder__dsgvo a { text-decoration: underline; }
      button[disabled] { opacity: 0.5; pointer-events: none; }
    `;
    embed.shadowRoot.appendChild(style);
  }

  // Zerlegt den Hinweistext und ersetzt bekannte Richtlinien-Nennungen durch
  // echte Verweise. Baut das DOM Knoten für Knoten, ohne innerHTML.
  #linkifyDisclaimer(text) {
    const KEYS = [
      { pattern: 'Datenschutzerklärung', href: '/policies/privacy-policy' },
      { pattern: 'allgemeinen Geschäftsbedingungen', href: '/policies/terms-of-service' },
      { pattern: 'Privacy Policy', href: '/policies/privacy-policy' },
      { pattern: 'Terms of Service', href: '/policies/terms-of-service' },
    ];
    const frag = document.createDocumentFragment();
    let cursor = 0;
    while (cursor < text.length) {
      let next = null;
      for (const { pattern, href } of KEYS) {
        const idx = text.indexOf(pattern, cursor);
        if (idx >= 0 && (!next || idx < next.idx)) next = { idx, pattern, href };
      }
      if (!next) {
        frag.appendChild(document.createTextNode(text.slice(cursor)));
        break;
      }
      if (next.idx > cursor) {
        frag.appendChild(document.createTextNode(text.slice(cursor, next.idx)));
      }
      const a = document.createElement('a');
      a.href = next.href;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = next.pattern;
      frag.appendChild(a);
      cursor = next.idx + next.pattern.length;
    }
    return frag;
  }

  // Sucht das Formular erneut, statt es zu merken: Shopify Forms baut es nach einem
  // Prüffehler neu auf, eine gehaltene Referenz zeigte dann auf einen abgehängten Baum.
  #refreshFormAnswers() {
    const form = this.querySelector('shopify-forms-embed')?.shadowRoot?.querySelector('form');
    if (form) this.#syncAnswersToFormFields(form);
  }

  #syncAnswersToFormFields(form) {
    // Zu jeder gerenderten Frage das passende Formularfeld vorbelegen.
    this.querySelectorAll('[data-metaobject-handle]').forEach((q) => {
      const handle = q.dataset.metaobjectHandle;
      const field = form.querySelector(`[id="custom#${handle}"]`);
      if (!field) return;
      const values = Array.from(q.querySelectorAll('.mk-finder__input:checked'))
        .map((i) => i.value)
        .join(',');
      field.value = values;
      field.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // ALLE Geschmack-Finder-Felder ausblenden, ob vorbelegt oder nicht — sie
    // tragen Auswertungsdaten, nichts zum Ausfüllen. Shopify Forms hüllt jedes
    // Feld in ein `_formFieldContainer_*`-div mit gehashter Klasse; deshalb über
    // den Präfix greifen und notfalls auf das Elternelement ausweichen.
    form.querySelectorAll('[id^="custom#geschmack_finder_"]').forEach((field) => {
      const wrapper = field.closest('[class*="_formFieldContainer_"], label, .field') || field.parentElement;
      if (wrapper) wrapper.style.display = 'none';
      else field.style.display = 'none';
    });
  }

  // Verschwindet das Formular aus dem Shadow-Root, hat Shopify Forms es
  // angenommen und zeigt an seiner Stelle die eigene Bestätigung. Die
  // Empfehlungen darüber bleiben stehen — es gibt keinen Schritt mehr,
  // in den wir weiterspringen müssten.
  #wireFormSubmit(embed, form) {
    new MutationObserver(() => {
      if (!embed.shadowRoot?.contains(form)) {
        this.#trackEvent('coffee_finder_form_submitted', 'form_submitted');
        if (window.fbq) window.fbq('trackCustom', 'lead_coffee_finder');
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            send_to: 'AW-790289825/FgUkCLWbqYsbEKG76_gC',
          });
        }
      }
    }).observe(embed.shadowRoot, { childList: true, subtree: true });
  }

  // === Auswertung ===

  #trackStartedOnce() {
    if (this.#tracked) return;
    this.#tracked = true;
    this.#trackEvent('coffee_finder_started', 'started');
  }

  #trackCurrentAnswer() {
    const stepEl = this.#steps[this.#current];
    if (!stepEl) return;
    const checked = stepEl.querySelector('.mk-finder__input:checked');
    if (!checked) return;
    this.#trackEvent(`coffee_finder_question_${this.#current}`, checked.value);
  }

  #trackEvent(eventName, value) {
    // Erstes Auslösen = 'coffee_finder'; jedes weitere unter demselben Namen =
    // 'coffee_finder_revised' (etwa wenn jemand zurückgeht und neu antwortet).
    const category = this.#trackedEventNames.has(eventName)
      ? 'coffee_finder_revised'
      : 'coffee_finder';
    this.#trackedEventNames.add(eventName);
    if (window.gtag) {
      window.gtag('event', eventName, { event_category: category, value });
    }
    if (window.fbq) {
      window.fbq('trackCustom', eventName);
    }
  }

  // === Sprachwahl ===

  #localized(de, en) {
    const locale = document.documentElement.lang || 'de';
    return locale.startsWith('de') ? de : en;
  }
}

if (!customElements.get('mk-geschmack-finder')) {
  customElements.define('mk-geschmack-finder', MkGeschmackFinder);
}
